import { useEffect, useRef, useState } from 'react';
import './MovableHelper.css';

const STORAGE_KEY = 'mhc:coach-position:v1';
const DEFAULT = { side: 'right', y: 0.4 };
const clamp = (n, min, max) => Math.max(min, Math.min(Math.max(min, max), n));
function savedPosition() {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (value && ['left', 'right'].includes(value.side) && Number.isFinite(value.y)) return { side: value.side, y: clamp(value.y, 0, 1) };
  } catch (_) { /* A blocked preference store must not prevent opening the Coach. */ }
  return DEFAULT;
}
function viewport() {
  const v = window.visualViewport;
  return { width: v?.width || window.innerWidth, height: v?.height || window.innerHeight, left: v?.offsetLeft || 0, top: v?.offsetTop || 0 };
}
export default function useHelperPosition(docked, onMove) {
  const [position, setPosition] = useState(savedPosition);
  const [screen, setScreen] = useState(viewport);
  const [dragging, setDragging] = useState(false);
  const gesture = useRef(null), suppressClick = useRef(false);
  useEffect(() => {
    const update = () => setScreen(viewport());
    window.addEventListener('resize', update);
    const visibleViewport = window.visualViewport;
    if (visibleViewport) {
      visibleViewport.addEventListener('resize', update);
      visibleViewport.addEventListener('scroll', update);
    }
    return () => {
      window.removeEventListener('resize', update);
      if (visibleViewport) {
        visibleViewport.removeEventListener('resize', update);
        visibleViewport.removeEventListener('scroll', update);
      }
    };
  }, []);
  const persist = value => { setPosition(value); try { localStorage.setItem(STORAGE_KEY, JSON.stringify(value)); } catch (_) {} };
  const extent = Math.max(1, screen.height - 60);
  const x = screen.left + (position.side === 'left' ? 8 : Math.max(8, screen.width - 52));
  const y = screen.top + 8 + extent * position.y;
  const panelWidth = Math.min(410, Math.max(0, screen.width - 16));
  const panelHeight = Math.min(670, Math.max(0, screen.height - 16));
  const rootStyle = docked ? undefined : { left: x, top: y, right: 'auto', bottom: 'auto' };
  const panelStyle = docked ? undefined : {
    position: 'fixed', width: panelWidth, height: panelHeight, maxHeight: panelHeight, margin: 0,
    left: clamp(position.side === 'left' ? x + 52 : x - panelWidth - 8, screen.left + 8, screen.left + screen.width - panelWidth - 8),
    top: clamp(y - panelHeight / 2 + 22, screen.top + 8, screen.top + screen.height - panelHeight - 8)
  };
  const moveTo = (event, initial) => ({
    side: event.clientX < screen.left + screen.width / 2 ? 'left' : 'right',
    y: clamp(initial.position.y + (event.clientY - initial.y) / extent, 0, 1)
  });
  const buttonProps = docked ? {} : {
    title: 'AI Coach — drag to move; arrow keys also move it',
    'aria-label': 'Open AI Coach. Drag or use arrow keys to move.',
    'aria-keyshortcuts': 'ArrowUp ArrowDown ArrowLeft ArrowRight',
    onPointerDown: event => {
      if ((event.button !== undefined && event.button !== 0) || event.isPrimary === false) return;
      suppressClick.current = false;
      gesture.current = { id: event.pointerId, x: event.clientX, y: event.clientY, position, moved: false };
      if (event.currentTarget.setPointerCapture) event.currentTarget.setPointerCapture(event.pointerId);
    },
    onPointerMove: event => {
      const g = gesture.current; if (!g || event.pointerId !== g.id) return;
      if (!g.moved && Math.hypot(event.clientX - g.x, event.clientY - g.y) < 7) return;
      if (!g.moved) { g.moved = true; setDragging(true); onMove(); }
      event.preventDefault(); setPosition(moveTo(event, g));
    },
    onPointerUp: event => {
      const g = gesture.current; if (!g || event.pointerId !== g.id) return;
      if (g.moved) { persist(moveTo(event, g)); suppressClick.current = true; }
      gesture.current = null; setDragging(false);
      if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    },
    onPointerCancel: () => {
      if (gesture.current) setPosition(gesture.current.position);
      gesture.current = null; suppressClick.current = true; setDragging(false);
    },
    onLostPointerCapture: () => {
      if (gesture.current) { setPosition(gesture.current.position); gesture.current = null; setDragging(false); }
    },
    onClickCapture: event => {
      if (suppressClick.current && event.detail !== 0) { event.preventDefault(); event.stopPropagation(); suppressClick.current = false; }
    },
    onKeyDown: event => {
      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault(); onMove();
      persist({ side: event.key === 'ArrowLeft' ? 'left' : event.key === 'ArrowRight' ? 'right' : position.side,
        y: clamp(position.y + (event.key === 'ArrowUp' ? -32 : event.key === 'ArrowDown' ? 32 : 0) / extent, 0, 1) });
    }
  };
  return { rootStyle, panelStyle, buttonProps, dragging };
}
